import { useState } from "react";
import { CalendarClock, X } from "lucide-react";

/**
 * SchedulePicker — pre-schedule a ride for the future. Two fields: date
 * + time, both controlled. Returns ISO 8601 UTC via `onChange(iso|null)`.
 * Helper text shows the lead-time window (15 min – 14 days).
 *
 * Minimal: leaves date/time validation to the backend. Min selectable is
 * 'today + 15 min' to avoid obvious past-time submissions.
 */
const pad = (n) => String(n).padStart(2, "0");

function minLocalISO(addMinutes = 15) {
  const d = new Date(Date.now() + addMinutes * 60_000);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function SchedulePicker({ value, onChange }) {
  const [open, setOpen] = useState(Boolean(value));
  const [local, setLocal] = useState(value ? value.slice(0, 16) : "");

  const apply = (val) => {
    setLocal(val);
    if (!val) {
      onChange?.(null);
      return;
    }
    // local datetime-local string → ISO UTC
    const iso = new Date(val).toISOString();
    onChange?.(iso);
  };

  if (!open) {
    return (
      <button
        data-testid="schedule-toggle-btn"
        type="button"
        onClick={() => setOpen(true)}
        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-body uppercase tracking-wider rounded border border-gold/30 text-gold/90 hover:bg-gold/10 transition-colors"
      >
        <CalendarClock size={12} /> Schedule for later
      </button>
    );
  }

  return (
    <div data-testid="schedule-picker"
      className="p-3 border border-gold/30 bg-black/30 rounded-md space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wider text-gold flex items-center gap-1.5">
          <CalendarClock size={12} /> Schedule pickup
        </p>
        <button
          data-testid="schedule-clear-btn"
          type="button"
          onClick={() => { apply(""); setOpen(false); }}
          className="text-white/50 hover:text-white"
        ><X size={14} /></button>
      </div>
      <input
        data-testid="schedule-datetime-input"
        type="datetime-local"
        min={minLocalISO(15)}
        value={local}
        onChange={(e) => apply(e.target.value)}
        className="w-full bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white focus:border-gold/60 focus:outline-none"
      />
      <p className="text-[10px] text-white/40">
        Anywhere from 15 min – 14 days out. We auto-dispatch 10 min before.
      </p>
    </div>
  );
}
